import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { getSettings, SettingsState } from '..';
import {
  CdnApiResponse,
  CdnChannelConfiguration,
  CdnInterfaceConfiguration,
  CdnServerConfigResponse,
  CdnUploadId,
} from '../models/cdn';

const rawBaseQuery = fetchBaseQuery();
const dynamicBaseQuery: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions,
) => {
  const settings = getSettings(api.getState() as SettingsState);
  const urlEnd = typeof args === 'string' ? args : args.url;
  const adjustedUrl = `${settings?.servers.serverCdn ?? ''}/api/v1/${urlEnd}`;
  const adjustedArgs = typeof args === 'string' ? adjustedUrl : { ...args, url: adjustedUrl };
  return rawBaseQuery(adjustedArgs, api, extraOptions);
};

const tags = {
  serverConfig: 'ServerConfiguration',
  networkConfig: 'NetworkConfiguration',
  channelsConfiguration: 'ChannelsConfiguration',
};
export const cdnApi = createApi({
  reducerPath: 'cdnApi',
  baseQuery: dynamicBaseQuery,
  tagTypes: [tags.serverConfig, tags.networkConfig, tags.channelsConfiguration],
  endpoints: builder => ({
    getCdnServerConfiguration: builder.query<CdnServerConfigResponse, void>({
      query: () => `server/configuration`,
      providesTags: [tags.serverConfig],
      transformResponse: (response: CdnApiResponse<CdnServerConfigResponse>) => response.data,
    }),
    getCdnNetworkConfiguration: builder.query<CdnInterfaceConfiguration[], void>({
      query: () => `network/configuration`,
      providesTags: [tags.networkConfig],
      transformResponse: (response: CdnApiResponse<CdnInterfaceConfiguration[]>) =>
        response.data.map(net => {
          delete net['updatedAt'];
          return net;
        }),
    }),
    updateCdnServerConfiguration: builder.mutation<void, CdnServerConfigResponse>({
      query: config => ({
        url: `server/configuration`,
        method: 'PUT',
        body: config,
      }),
      invalidatesTags: [tags.serverConfig],
    }),
    updateCdnNetworkConfiguration: builder.mutation<void, CdnInterfaceConfiguration[]>({
      query: config => ({
        url: `network/configuration`,
        method: 'PUT',
        body: config,
      }),
      invalidatesTags: [tags.networkConfig],
    }),
    getChannelsConfiguration: builder.query<CdnChannelConfiguration[], void>({
      query: () => `channels/configuration`,
      providesTags: [tags.channelsConfiguration],
      transformResponse: (response: CdnApiResponse<CdnChannelConfiguration[]>) =>
        response.data.map(ch => {
          delete ch['updatedAt'];
          delete ch['createdAt'];
          return ch;
        }),
    }),
    deleteChannelConfiguration: builder.mutation<void, CdnChannelConfiguration>({
      query: config => ({
        url: `channel/configuration/${config.id}`,
        method: 'DELETE',
        body: config,
      }),
      invalidatesTags: [tags.channelsConfiguration],
    }),
    updateChannelConfiguration: builder.mutation<void, CdnChannelConfiguration>({
      query: config => ({
        url: `channel/configuration/${config.id}`,
        method: 'PUT',
        body: config,
      }),
      invalidatesTags: [tags.channelsConfiguration],
    }),
    createChannelsConfiguration: builder.mutation<void, CdnChannelConfiguration[]>({
      query: configs => ({ url: 'channels/configuration', method: 'POST', body: configs }),
      invalidatesTags: [tags.channelsConfiguration],
    }),
    uploadCertificate: builder.mutation<string, FormData>({
      query: formData => ({
        url: 'server/certificate/upload',
        method: 'POST',
        body: formData,
      }),
      transformResponse: (response: CdnApiResponse<CdnUploadId>) => response.data.id,
    }),
    reloadCdnServer: builder.mutation<void, void>({
      query: () => ({
        url: `server/reload`,
        method: 'PATCH',
      }),
    }),
    restartCdnServer: builder.mutation<void, void>({
      query: () => ({
        url: `server/restart`,
        method: 'PATCH',
      }),
    }),
  }),
});

export const {
  useGetCdnServerConfigurationQuery,
  useGetCdnNetworkConfigurationQuery,
  useUpdateCdnNetworkConfigurationMutation,
  useUpdateCdnServerConfigurationMutation,
  useGetChannelsConfigurationQuery,
  useUpdateChannelConfigurationMutation,
  useDeleteChannelConfigurationMutation,
  useCreateChannelsConfigurationMutation,
  useUploadCertificateMutation,
  useReloadCdnServerMutation,
  useRestartCdnServerMutation,
} = cdnApi;
